import { useEffect, useState } from 'react';
import type { StakeholderOption } from '@todo/shared';
import { stakeholderApi } from '../api/stakeholder.api';
import { Dropdown } from './ui';

/**
 * "+ Add from stakeholders…" - one dropdown, reused by the Meetings form
 * and the CG Sign recipient list.
 *
 * The LABEL ("Priya Raman - Donor") is built server-side and rendered as
 * given, so the same person reads identically wherever they are picked.
 *
 * ADMIN-ONLY DATA. /api/stakeholders/options 403s for everyone else, so
 * the component fetches nothing and renders nothing unless `enabled` is
 * true - a dropdown that always 403s is worse than no dropdown.
 */
export function StakeholderSelect({
  enabled, onPick, label = 'Add from stakeholders', exclude = [], options,
}: {
  /** Pass `isAdmin` - the API is the real control, this avoids a dead UI. */
  enabled: boolean;
  onPick: (option: StakeholderOption) => void;
  label?: string;
  /** Ids already chosen - hidden rather than shown and then rejected. */
  exclude?: string[];
  /**
   * Supplied by the caller when the organisation is not simply "the one I
   * am signed into" - the Meetings form, where an org dropdown decides
   * whose contact book this is. Omitted, the component loads the current
   * org's, which is what CG Sign still wants.
   */
  options?: StakeholderOption[];
}) {
  const [fetched, setFetched] = useState<StakeholderOption[]>([]);

  useEffect(() => {
    if (!enabled || options) return;
    stakeholderApi.options().then(setFetched).catch(() => setFetched([]));
  }, [enabled, options]);

  const available = (options ?? fetched).filter((o) => !exclude.includes(o.id));
  if (!enabled || available.length === 0) return null;

  /* The shared Dropdown, so this matches every other picker and gains
     the search box - a contact book is the list most in need of one. */
  return (
    <Dropdown
      className="mt-2"
      value={undefined}
      onChange={(pickedId) => {
        const picked = available.find((o) => o.id === pickedId);
        if (picked) onPick(picked);
      }}
      placeholder={label}
      searchPlaceholder="Search stakeholders…"
      options={available.map((o) => ({
        value: o.id,
        /* `label` is built server-side ("Priya Raman - Donor") so the same
           person reads identically wherever they are picked. */
        label: o.label,
        description: [o.organisationName, o.email].filter(Boolean).join(' · ') || undefined,
      }))}
    />
  );
}
